import * as React from 'react';
import { useQuery } from '@apollo/client';
import classNames from 'classnames';
import { noop } from 'lodash';
import validator from 'validator';
import { CustomPanel, DynamicResultTable } from '@css-xp/base';
import { FormattedDate } from '@css-xp/base/formattedV2';
import { generatedTypesNew } from '@css-xp/graphtypes';
import { TermPaymentType } from '@atlassian/cssxp-types-billing';
import { DATE_TIME_FORMAT } from '@atlassian/cssxp-typescommon';
import { DEFAULT_ROWS_PER_PAGE, NOT_AVAILABLE, NO_DETAILS_TO_SHOW } from '@atlassian/cssxp-utils-entitlement';
import { fetchTermPaymentAuditTrails } from '../queries/fetch-term-payment-audit-trail';

export interface AuditNotesProps {
  id: string;
  label?: string;
  netTerm: TermPaymentType;
  taxId?: string | undefined | null;
}

interface SearchParamsArgs {
  id: string;
  netTerm: TermPaymentType;
  taxId?: string | undefined | null;
}

const headers = ['Date', 'Update', 'Details', 'Actioned by'];

const wrapInCell = (node: React.ReactElement | string) => {
  return <div className="audit-note-cell">{node}</div>;
};

const head = () => {
  const cells = headers?.map((header) => {
    return {
      content: wrapInCell(header),
    };
  });
  return {
    cells,
  };
};

const column = (auditNotes: generatedTypesNew.TermPaymentAuditTrail[] | undefined) => {
  return auditNotes?.map((auditNote, index) => {
    const { createdAt, revisionType, updatedBy, comments } = auditNote;
    return {
      key: `audit-note-v2-${index}`,
      cells: [
        {
          content: wrapInCell(
            <FormattedDate date={createdAt ? Math.floor(createdAt).toString() : ''} dateFormat={DATE_TIME_FORMAT} />
          ),
        },
        {
          content: wrapInCell(<span>{revisionType ?? NOT_AVAILABLE}</span>),
        },
        {
          content: wrapInCell(<span>{comments ? comments : NOT_AVAILABLE}</span>),
        },
        {
          content: wrapInCell(
            <div
              className={classNames({
                email: updatedBy && validator.isEmail(updatedBy ?? ''),
              })}
            >
              {updatedBy ? updatedBy : NOT_AVAILABLE}
            </div>
          ),
        },
      ],
    };
  });
};

const getEmptyStateProps = (error = '') => {
  const emptyStateProps = {
    header: error ? 'Oh no! Something went wrong, please try again ' : NO_DETAILS_TO_SHOW,
    description: error,
  };
  return emptyStateProps;
};

// const defaultSortOptions = {
//   sortFields: ['created', 'id'],
//   sortFieldsDirections: [generatedTypesNew.SortDirection.DESC, generatedTypesNew.SortDirection.DESC],
// };

// const defaultAuditNotesSearchOptions: generatedTypesNew.FetchAuditNotesForEntityQueryVariables['searchOptions'] = {
//   first: DEFAULT_ROWS_PER_PAGE,
//   sort: defaultSortOptions,
// };

const defaultSearchOptions = {
  page: 0,
  pageSize: DEFAULT_ROWS_PER_PAGE,
};

export const TermDetailsAuditTrailTab = ({ id, taxId, label = 'Audit trail', netTerm }: AuditNotesProps) => {
  const [rowsPerPage, setRowsPerPage] = React.useState<number>(DEFAULT_ROWS_PER_PAGE);
  const [currentPage, setCurrentPage] = React.useState<number>(1);
  const buildSearchParams = ({
    id,
  }: SearchParamsArgs): generatedTypesNew.TermPaymentAuditTrailSearchParams | undefined => {
    const transactionAccountKey = netTerm === generatedTypesNew.TermPaymentTypes.NET14 ? id : taxId;
    return {
      transactionAccountKey,
    };
  };

  const searchParams = buildSearchParams({ id, netTerm, taxId });
  const searchOptions = defaultSearchOptions;

  const { data, loading, error, refetch } = useQuery<
    generatedTypesNew.FetchTermPaymentAuditTrailsQuery,
    generatedTypesNew.FetchTermPaymentAuditTrailsQueryVariables
  >(fetchTermPaymentAuditTrails, {
    variables: {
      searchParams,
      searchOptions,
    },
    displayName: 'fetchTermPaymentAuditTrails',
    notifyOnNetworkStatusChange: true,
  });

  const queryResults = data?.termPaymentAuditTrails;
  const auditLogs = queryResults?.nodes as generatedTypesNew.TermPaymentAuditTrail[];

  const totalCount = queryResults?.totalCount ?? 0;
  const columns = error ? [] : column(auditLogs);

  const onRowsPerPageChange = async (rowsCount: number) => {
    setRowsPerPage(rowsCount);
    await refetch({
      searchOptions: {
        page: 0,
        pageSize: rowsCount,
      },
    });
    setCurrentPage(1);
  };

  const onPageChange = React.useCallback(
    async (newPage: number) => {
      setCurrentPage(newPage);
      await refetch({
        searchOptions: {
          page: newPage - 1,
          pageSize: rowsPerPage,
        },
      });
    },
    [rowsPerPage, refetch, currentPage]
  );

  return (
    <div className="audit-notes-v2-container" data-testid="audit-notes-txa-log">
      <CustomPanel
        testId="audit-trail-txa"
        heading={label}
        expandedHeader={`Collapse to hide audit trail for this account balance`}
        collapsedHeader={`Expand to view audit trail for this account balance`}
      >
        <div className="custom-design">
          <DynamicResultTable
            head={head()}
            rows={columns}
            loadingSpinnerSize="small"
            totalCount={totalCount}
            emptyStateProps={getEmptyStateProps(error && error.toString())}
            isLoading={loading}
            isPaginationEnabled={totalCount > DEFAULT_ROWS_PER_PAGE}
            rowsPerPage={rowsPerPage}
            currentPage={currentPage}
            onPageChange={onPageChange}
            onRowChange={onRowsPerPageChange}
          ></DynamicResultTable>
        </div>
      </CustomPanel>
    </div>
  );
};
